import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    if (user && user.role === "admin") {
        const to = location.state?.from?.pathname || "/admin";
        return <Navigate to={to} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email.trim().toLowerCase(), password);
        } catch (err) {
            setError(err.message || "Connexion impossible");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-6 bg-lpl-bg-alt"
            data-testid="admin-login-page"
        >
            {/* Decorative shapes */}
            <div
                aria-hidden
                className="pointer-events-none fixed -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-lpl-lavender/30"
            />
            <div
                aria-hidden
                className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-lpl-primary/15"
            />

            <div className="relative w-full max-w-md">
                <div
                    aria-hidden
                    className="absolute -bottom-5 -right-5 w-full h-full bg-lpl-primary-deep"
                />
                <form
                    onSubmit={submit}
                    className="relative bg-lpl-bg p-8 md:p-10 shadow-xl"
                    data-testid="admin-login-form"
                >
                    <p className="font-serif italic text-xl text-lpl-terracotta mb-2">
                        Panel privé
                    </p>
                    <h1 className="font-display font-semibold text-4xl md:text-5xl text-lpl-text leading-[0.95] tracking-tight mb-1">
                        Bienvenue,
                    </h1>
                    <p className="font-serif italic text-3xl text-lpl-primary-deep mb-8">
                        Sylvie.
                    </p>

                    <label className="block mb-5">
                        <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                            Adresse email
                        </span>
                        <input
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 bg-lpl-bg-alt border-2 border-lpl-border focus:border-lpl-text outline-none font-body text-base text-lpl-text"
                            data-testid="admin-login-email"
                        />
                    </label>

                    <label className="block mb-7">
                        <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                            Mot de passe
                        </span>
                        <input
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 bg-lpl-bg-alt border-2 border-lpl-border focus:border-lpl-text outline-none font-body text-base text-lpl-text"
                            data-testid="admin-login-password"
                        />
                    </label>

                    {error && (
                        <p
                            className="mb-5 px-4 py-3 bg-lpl-terracotta/15 border-l-4 border-lpl-terracotta text-lpl-terracotta text-sm font-medium"
                            data-testid="admin-login-error"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-3 bg-lpl-primary text-lpl-text px-7 py-4 rounded-full text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors disabled:opacity-60"
                        data-testid="admin-login-submit"
                    >
                        {loading ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <LogIn size={18} />
                        )}
                        Se connecter
                    </button>

                    <p className="mt-6 text-center text-xs text-lpl-text-muted">
                        Accès réservé à l'administration de La Petite Lunetterie.
                    </p>
                </form>
            </div>
        </div>
    );
}
